import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import AuditableEntity from "@/types/AuditableEntity";

export class DateGroupEnum {
  TODAY = "Today";
  YESTERDAY = "Yesterday";
  PREVIOUS_7_DAYS = "Last 7 days";
  THIS_MONTH = "This month";
  MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  PREVIOUS_YEAR = "Prev. year";
}

export class InternationalizedDateGroupEnum {
  constructor(private t: (key: string) => string) {}

  get TODAY() {
    return this.t("conversationsList.dateGroups.today");
  }

  get YESTERDAY() {
    return this.t("conversationsList.dateGroups.yesterday");
  }

  get PREVIOUS_7_DAYS() {
    return this.t("conversationsList.dateGroups.last7Days");
  }

  get THIS_MONTH() {
    return this.t("conversationsList.dateGroups.thisMonth");
  }

  get MONTHS() {
    return [
      this.t("conversationsList.dateGroups.months.january"),
      this.t("conversationsList.dateGroups.months.february"),
      this.t("conversationsList.dateGroups.months.march"),
      this.t("conversationsList.dateGroups.months.april"),
      this.t("conversationsList.dateGroups.months.may"),
      this.t("conversationsList.dateGroups.months.june"),
      this.t("conversationsList.dateGroups.months.july"),
      this.t("conversationsList.dateGroups.months.august"),
      this.t("conversationsList.dateGroups.months.september"),
      this.t("conversationsList.dateGroups.months.october"),
      this.t("conversationsList.dateGroups.months.november"),
      this.t("conversationsList.dateGroups.months.december"),
    ];
  }

  get PREVIOUS_YEAR() {
    return this.t("conversationsList.dateGroups.previousYear");
  }
}

export const DATE_GROUPS = new DateGroupEnum();

/**
 * Extracts the date of an ISO date string
 * return: RegExpMatchArray -> [yyyy-mm-dd, yyyy, mm, dd]
 * */
export function extractYearMonthDay(ISODateString: string): RegExpMatchArray {
  return ISODateString?.split("T")[0].match(/(\d{4})-(\d{2})-(\d{2})/);
}

/**
 * Extracts the hour and minutes of an ISO date string
 * return: RegExpMatchArray -> [hh-mm, hh, mm]
 * */
export function extractHourAndMinutes(ISODateString: string): RegExpMatchArray {
  return ISODateString?.split("T")[1].match(/(\d{2}):(\d{2})/);
}

const [_, CURRENT_YEAR, CURRENT_MONTH, CURRENT_DAY] = extractYearMonthDay(new Date().toISOString());

export function getElementDateGroup<T extends AuditableEntity>(element: T): string {
  const createdAt = element.createdAt!;
  const [_, year, month, day]: string[] = extractYearMonthDay(createdAt);
  const dateGroups = new DateGroupEnum();
  let groupToReturn = "";
  if (year === CURRENT_YEAR) {
    if (month === CURRENT_MONTH) {
      if (day === CURRENT_DAY) {
        groupToReturn = dateGroups.TODAY;
      } else if (Number(day) === Number(CURRENT_DAY) - 1) {
        groupToReturn = dateGroups.YESTERDAY;
      } else if (Number(day) >= Number(CURRENT_DAY) - 7) {
        groupToReturn = dateGroups.PREVIOUS_7_DAYS;
      } else {
        groupToReturn = dateGroups.THIS_MONTH;
      }
    } else {
      groupToReturn = dateGroups.MONTHS[Number(month) - 1];
    }
  } else {
    groupToReturn += dateGroups.PREVIOUS_YEAR;
    groupToReturn += ", " + dateGroups.MONTHS[Number(month) - 1];
  }
  return groupToReturn;
}

export function getElementDateGroupInternationalized<T extends AuditableEntity>(element: T, t: (key: string) => string): string {
  const createdAt = element.createdAt!;
  const [_, year, month, day]: string[] = extractYearMonthDay(createdAt);
  const dateGroups = new InternationalizedDateGroupEnum(t);
  let groupToReturn = "";
  if (year === CURRENT_YEAR) {
    if (month === CURRENT_MONTH) {
      if (day === CURRENT_DAY) {
        groupToReturn = dateGroups.TODAY;
      } else if (Number(day) === Number(CURRENT_DAY) - 1) {
        groupToReturn = dateGroups.YESTERDAY;
      } else if (Number(day) >= Number(CURRENT_DAY) - 7) {
        groupToReturn = dateGroups.PREVIOUS_7_DAYS;
      } else {
        groupToReturn = dateGroups.THIS_MONTH;
      }
    } else {
      groupToReturn = dateGroups.MONTHS[Number(month) - 1];
    }
  } else {
    groupToReturn += dateGroups.PREVIOUS_YEAR;
    groupToReturn += ", " + dateGroups.MONTHS[Number(month) - 1];
  }
  return groupToReturn;
}

/**
 * This class is made with the idea to group elements that have an available createdAt attribute inside pre-established
 * date groups
 * */
export default class DateGroups<T extends AuditableEntity> extends Map<string, T[]> {
  constructor(entityArray: T[]) {
    super();
    for (let i = 0; i < entityArray.length; i++) {
      const entity = entityArray[i];
      this.addNewElementChecked(getElementDateGroup(entity).toString(), entity);
    }
  }

  static createInternationalized<T extends AuditableEntity>(entityArray: T[], t: (key: string) => string): DateGroups<T> {
    const instance = new DateGroups<T>([]);
    for (let i = 0; i < entityArray.length; i++) {
      const entity = entityArray[i];
      instance.addNewElementChecked(getElementDateGroupInternationalized(entity, t), entity);
    }
    return instance;
  }

  addNewElementChecked(header: string, value: T) {
    if (this.has(header)) {
      this.get(header)!.push(value);
    } else {
      this.set(header, [value]);
    }
  }

  shiftElementInsideDateGroup(newElement: T) {
    const dateGroup: string = getElementDateGroup(newElement);
    if (this.has(dateGroup)) {
      this.get(dateGroup)!.unshift(newElement);
    } else {
      this.set(dateGroup, [newElement]);
    }
  }

  /**
   * Returns an array from the DateGroup object with the format:
   *
   * [{groupName: group1}, element1, element2, element3, ..., {groupName: groupN}, elementM, elementM+1]
   * */
  toCompactArray() {
    const compactArray = [];
    for (const [group, elements] of Array.from(this.entries())) {
      compactArray.push({ dateGroupName: group }, ...elements);
    }
    return compactArray;
  }
}

export function calculateMessageDateGroup(message: LazoChatMessage) {
  const [_, h, m] = extractHourAndMinutes(message.createdAt!);
  const hoursAndMinutes = h.toString() + ":" + m.toString();
  const dateGroup = getElementDateGroup<LazoChatMessage>(message);
  return dateGroup + ", " + hoursAndMinutes;
}
